import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useApi } from '../lib/useApi';
import { useMutation } from '../lib/useMutation';
import { money, shortDate } from '../lib/format';
import { ExploreTabs } from '../components/ExploreTabs';
import { PublicHeader } from '../components/PublicHeader';
import { SiteFooter } from '../components/SiteFooter';
import {
  Alert, Button, Card, EmptyState, ErrorBlock, LoadingBlock, StatusPill,
} from '../components/ui';
import '../styles/public.css';

/**
 * What I have posted (specification §27, first slice).
 *
 * The reason this page has to exist is small and important: until migration
 * 056, somebody who registered a business could not see it again. The public
 * policy admits approved rows only, so their own pending listing was invisible
 * to them, and the only way to learn what had happened was to search the
 * directory and not find themselves.
 *
 * So the page leads with STATUS, not with the listing. "Waiting for review" is
 * the answer to the question they actually came with.
 */

interface Service {
  id: string; slug: string; business_name: string; status: string;
  county: string; town: string | null; submitted_at: string;
  reviewed_at: string | null; review_note: string | null; category_name: string;
}
interface Item {
  id: string; slug: string; title: string; status: string; price: string | null;
  county: string; town: string | null; submitted_at: string;
  reviewed_at: string | null; review_note: string | null;
  expires_at: string; category_name: string;
}

interface Home {
  id: string; title: string; status: string; rent: string; stay_type: string;
  county: string | null; estate_area: string | null; rejection_reason: string | null;
  paid_until: string | null; published_at: string | null; created_at: string;
}
interface SaleAd {
  id: string; title: string; status: string; asking_price: string;
  county: string | null; estate_area: string | null; paid_until: string | null;
  published_at: string | null; created_at: string;
}

/** Where a property advert has got to, in the poster's words (156). */
function homeMeaning(h: { status: string; paid_until: string | null }): string {
  const paid = !!h.paid_until && new Date(h.paid_until) > new Date();
  if (h.status === 'draft') return 'Draft. Finish it, pay and send it for approval.';
  if (h.status === 'pending_review') return paid ? 'Paid. Waiting for our approval.' : 'Waiting for our approval. It also needs paying for before it can go up.';
  if (h.status === 'published' || h.status === 'approved') return paid ? 'Live. People can find it and contact you.' : 'Approved. Pay to put it up.';
  if (h.status === 'rejected') return 'Not approved. Open it to see why.';
  return h.status.replace(/_/g, ' ');
}

const ROLES: [string, string][] = [['owner', 'Property owner'], ['agent', 'Agent'], ['caretaker', 'Caretaker'], ['individual', 'Individual']];
const stateTone = (status: string) => status === 'published' ? 'approved' : status === 'pending_review' ? 'pending' : status;

const TONE: Record<string, 'success' | 'warning' | 'danger' | 'info'> = {
  approved: 'success', pending: 'warning', rejected: 'danger',
  suspended: 'danger', sold: 'info', expired: 'info',
};

/** What each state means to the person who posted it, in their words. */
const MEANING: Record<string, string> = {
  pending: 'Waiting for review. Nobody can see it yet.',
  approved: 'Live — people can find it and call you.',
  rejected: 'Not published.',
  suspended: 'Taken down.',
  sold: 'Marked sold. The advert stays up so links still work.',
  expired: 'Past its 60 days and no longer shown.',
};

export function MyListings() {
  const { pathname } = useLocation();
  const view = useApi<{ services: Service[]; items: Item[]; homes?: Home[]; sales?: SaleAd[]; advertiserRole?: string | null }>('/api/v1/my-listings');
  const setRole = useMutation();
  const sold = useMutation();
  const [notice, setNotice] = useState<string | null>(null);

  const markSold = async (item: Item) => {
    if (!window.confirm(`Mark "${item.title}" as sold?\n\nIt stays visible, marked sold, so anyone who saved the link still gets an answer.`)) return;
    setNotice(null);
    try {
      await sold.run(`/api/v1/my-listings/items/${item.id}/sold`, { body: {} });
      setNotice(`${item.title} is marked sold.`);
      view.reload();
    } catch { /* surfaced by the mutation */ }
  };

  const homes = view.data?.homes ?? [];
  const sales = view.data?.sales ?? [];
  const nothing = (view.data?.services.length ?? 0) === 0 && (view.data?.items.length ?? 0) === 0
    && homes.length === 0 && sales.length === 0;
  const role = view.data?.advertiserRole ?? null;

  return (
    <>
      <PublicHeader />
      <main className="page public-page">
        <ExploreTabs path={pathname} />

        <div className="page-head">
          <div>
            <h1 className="page-title">My listings</h1>
            <p className="page-sub">Everything you have posted, and where it has got to.</p>
          </div>
          <div className="row">
            <Link className="btn btn-primary" to="/my-listings/homes/new">Post a home to let</Link>
            <Link className="btn btn-secondary" to="/my-listings/sales/new">Post a property for sale</Link>
            <Link className="btn btn-ghost" to="/post">Post something else</Link>
          </div>
        </div>

        {/* Who is posting property: shown on every advert (156). Asked here
            for accounts made before sign-up asked it. */}
        {view.data && (
          <div style={{ marginBottom: 16 }}>
            <Card>
              <div className="row" style={{ alignItems: 'center', gap: 8 }}>
                <span>Posting property as</span>
                <select className="input" style={{ maxWidth: 200 }} value={role ?? ''} disabled={setRole.busy}
                  onChange={async (e) => {
                    if (!e.target.value) return;
                    try { await setRole.run('/api/v1/my-listings/role', { method: 'PUT', body: { role: e.target.value } }); view.reload(); }
                    catch { /* shown below */ }
                  }}>
                  <option value="">Choose one</option>
                  {ROLES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
                <span className="muted small">Shown on your adverts. Every property advert is paid for and approved by NyumbaSuite before it goes live.</span>
              </div>
              {setRole.error && <Alert tone="danger">{setRole.error.message}</Alert>}
            </Card>
          </div>
        )}

        {notice && <div style={{ marginBottom: 16 }}><Alert tone="success">{notice}</Alert></div>}
        {sold.error && <div style={{ marginBottom: 16 }}><Alert tone="danger">{sold.error.message}</Alert></div>}

        {view.loading ? <LoadingBlock />
          : view.error ? <ErrorBlock error={view.error} onRetry={view.reload} />
          : nothing ? (
            <EmptyState
              title="You have not posted anything yet"
              message="Post a home to let or a property for sale (paid, and approved before it goes live), or offer a service or sell something for free."
            />
          ) : (
            <>
              {homes.length > 0 && (
                <Card title="Homes to let and short stays" bodyClassName="card-pad-0">
                  <div className="stack" style={{ gap: 0 }}>
                    {homes.map(h => (
                      <div key={h.id} className="my-listing">
                        <div>
                          <p className="my-listing-title"><Link to={`/my-listings/homes/${h.id}`}>{h.title}</Link></p>
                          <p className="provider-meta">
                            {h.stay_type === 'short_stay' ? `${money(h.rent)} a night` : `${money(h.rent)} a month`}
                            {h.estate_area || h.county ? ` · ${[h.estate_area, h.county].filter(Boolean).join(', ')}` : ''}
                            {' · created '}{shortDate(h.created_at)}
                          </p>
                          {h.rejection_reason && <p className="provider-meta">Reason: {h.rejection_reason}</p>}
                        </div>
                        <div className="my-listing-state">
                          <StatusPill status={h.status.replace(/_/g, ' ')} tone={TONE[stateTone(h.status)] ?? 'info'} />
                          <span className="provider-meta">{homeMeaning(h)}</span>
                          {!(h.paid_until && new Date(h.paid_until) > new Date()) && h.status !== 'rejected' && (
                            <Link className="provider-meta" to="/my-listings/homes/bundles">Pay to advertise</Link>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </Card>
              )}

              {sales.length > 0 && (
                <Card title="Property for sale" bodyClassName="card-pad-0">
                  <div className="stack" style={{ gap: 0 }}>
                    {sales.map(x => (
                      <div key={x.id} className="my-listing">
                        <div>
                          <p className="my-listing-title"><Link to={`/my-listings/sales/${x.id}`}>{x.title}</Link></p>
                          <p className="provider-meta">
                            {money(x.asking_price)}
                            {x.estate_area || x.county ? ` · ${[x.estate_area, x.county].filter(Boolean).join(', ')}` : ''}
                          </p>
                        </div>
                        <div className="my-listing-state">
                          <StatusPill status={x.status.replace(/_/g, ' ')} tone={TONE[stateTone(x.status)] ?? 'info'} />
                          <span className="provider-meta">{homeMeaning(x)}</span>
                          {x.status !== 'draft' && x.status !== 'rejected' && !(x.paid_until && new Date(x.paid_until) > new Date()) && (
                            <Link className="provider-meta" to={`/my-listings/sales/${x.id}/pay`}>Pay to publish</Link>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </Card>
              )}

              {(view.data!.services.length > 0) && (
                <Card title="Services" bodyClassName="card-pad-0">
                  <div className="stack" style={{ gap: 0 }}>
                    {view.data!.services.map(s => (
                      <div key={s.id} className="my-listing">
                        <div>
                          <p className="my-listing-title">
                            {s.status === 'approved'
                              ? <Link to={`/services/${s.slug}`}>{s.business_name}</Link>
                              : s.business_name}
                          </p>
                          <p className="provider-meta">
                            {s.category_name} · {s.town ? `${s.town}, ` : ''}{s.county}
                            {' · sent '}{shortDate(s.submitted_at)}
                          </p>
                          {s.review_note && (
                            <p className="provider-meta">“{s.review_note}”</p>
                          )}
                        </div>
                        <div className="my-listing-state">
                          <StatusPill status={s.status} tone={TONE[s.status] ?? 'info'} />
                          <span className="provider-meta">{MEANING[s.status]}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </Card>
              )}

              {(view.data!.items.length > 0) && (
                <Card title="Things for sale" bodyClassName="card-pad-0">
                  <div className="stack" style={{ gap: 0 }}>
                    {view.data!.items.map(i => (
                      <div key={i.id} className="my-listing">
                        <div>
                          <p className="my-listing-title">
                            {i.status === 'approved' || i.status === 'sold'
                              ? <Link to={`/marketplace/${i.slug}`}>{i.title}</Link>
                              : i.title}
                          </p>
                          <p className="provider-meta">
                            {i.category_name}
                            {i.price === null ? ' · Contact for price' : ` · ${money(i.price)}`}
                            {' · '}{i.town ? `${i.town}, ` : ''}{i.county}
                          </p>
                          <p className="provider-meta">
                            Sent {shortDate(i.submitted_at)} · runs until {shortDate(i.expires_at)}
                          </p>
                          {i.review_note && (
                            <p className="provider-meta">“{i.review_note}”</p>
                          )}
                        </div>
                        <div className="my-listing-state">
                          <StatusPill status={i.status} tone={TONE[i.status] ?? 'info'} />
                          <span className="provider-meta">{MEANING[i.status]}</span>
                          {/* The one edit a seller may make. Live items only —
                              there is nothing to withdraw from a listing the
                              public has never seen. */}
                          {i.status === 'approved' && (
                            <Button variant="ghost" loading={sold.busy}
                              onClick={() => markSold(i)}>Mark sold</Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </Card>
              )}
            </>
          )}
      </main>
      <SiteFooter />
    </>
  );
}
